import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { bandHeading, getChallengeAnalytics } from '../../../utils/incentiveChallengeApi';
import { CHALLENGE_TYPE_LABELS, formatChallengeValue } from '../../../utils/challengeFormat';
import RoundBoard from './RoundBoard';
import PrizeWonChip from './PrizeWonChip';
import TeamMembersDialog from './TeamMembersDialog';

/**
 * A challenge's prize board: who achieved what, and the one prize each won.
 *
 * Prizes do not add up — a person takes the prize of the highest rung they
 * cleared and only that — so the board is one row per person rather than a
 * column block per prize (changed with the user 2026-10-06).
 *
 * Target groups are a FILTER. Picking one narrows the board to the people
 * carrying that revenue target; "All" shows every group.
 *
 * People whose revenue target falls outside every group are not shown: they
 * cannot win anything, and a row of them would only raise the question of why
 * they are there.
 */

export default function BandBoardDialog({ challenge, open, setOpen }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  /** Which target group is on screen; 'all' until Finance narrows it. */
  const [band, setBand] = useState('all');
  /** The head whose team is open, on a head-role challenge. */
  const [teamOf, setTeamOf] = useState(null);

  useEffect(() => {
    if (!open || !challenge) {
      setAnalytics(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBand('all');
    getChallengeAnalytics(challenge.id)
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 'success') setAnalytics(res.data);
        else setError(res?.message || 'Could not load this challenge');
      })
      .catch((err) => !cancelled && setError(err?.message || 'Could not load this challenge'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, challenge]);

  const bands = useMemo(() => analytics?.bands || [], [analytics]);
  // One group needs no filter — there is nothing to choose between, and the
  // heading would only repeat what the single set of columns already says.
  const filterable = bands.length > 1;

  /** The people of each group, ranked by what they collected. */
  const peopleByBand = useMemo(() => {
    const byBand = new Map();
    for (const p of analytics?.participants || []) {
      if (!p.band) continue;
      const key = p.band.incentive_challenge_band_id;
      if (!byBand.has(key)) byBand.set(key, []);
      byBand.get(key).push(p);
    }
    for (const people of byBand.values()) {
      people.sort((a, b) => Number(b.achieved) - Number(a.achieved));
    }
    return byBand;
  }, [analytics]);

  /** A head-role challenge: each row is a head with a team behind the figure. */
  const hasTeams = useMemo(
    () => (analytics?.participants || []).some((p) => (p.members || []).length > 0),
    [analytics],
  );

  /** The people in view, ranked by what they achieved. */
  const rows = useMemo(() => {
    const visible = band === 'all'
      ? bands
      : bands.filter((b) => b.incentive_challenge_band_id === band);
    return visible
      .flatMap((b) => (peopleByBand.get(b.incentive_challenge_band_id) || []).map((p) => ({ ...p, group: b })))
      .sort((a, b) => Number(b.achieved) - Number(a.achieved));
  }, [bands, band, peopleByBand]);
  const type = analytics?.challenge?.challenge_type || challenge?.type;
  const selected = band === 'all' ? null : bands.find((b) => b.incentive_challenge_band_id === band);

  if (!challenge) return null;

  return (
    <GlobalDialog
      maxWidth="xl"
      fullWidth
      title={challenge.name}
      open={open}
      setOpen={setOpen}
      closeButton
      closeCondition={() => setOpen(false)}
      content={
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {CHALLENGE_TYPE_LABELS[type] || type} · {challenge.scopeLabel} ·{' '}
            {challenge.assignmentLabel}
          </Typography>

          {loading && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          )}

          {/* Judged round by round: "who won what" is a person × round grid,
              not one column per prize — the same prize is won many times. */}
          {!loading && !error && analytics?.summary?.partition && (
            <RoundBoard analytics={analytics} challenge={challenge} />
          )}

          {!loading && !error && !analytics?.summary?.partition && (
            <>
              {filterable && (
                <Box sx={{ mb: 1.5 }}>
                  <TextField
                    select
                    size="small"
                    label="Target group"
                    value={band}
                    onChange={(e) => setBand(e.target.value)}
                    sx={{ minWidth: 260 }}
                  >
                    <MenuItem value="all">All target groups</MenuItem>
                    {bands.map((b) => (
                      <MenuItem
                        key={b.incentive_challenge_band_id}
                        value={b.incentive_challenge_band_id}
                      >
                        {bandHeading(b)}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
                    {selected ? bandHeading(selected) : 'All target groups'}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}
                    >
                      {selected
                        ? `${(peopleByBand.get(selected.incentive_challenge_band_id) || []).length} people`
                        : `${bands.length} groups`}
                    </Typography>
                  </Typography>
                </Box>
              )}

              {rows.length === 0 && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Nobody has been placed in a target group yet.
                  </Typography>
                </Box>
              )}

              {rows.length > 0 && (
                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 48 }}>#</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Role</TableCell>
                        {/* Which group placed them. Only on All: with a group
                            picked the heading above already says it. */}
                        {band === 'all' && filterable && <TableCell>Target group</TableCell>}
                        <TableCell align="right">Achieved</TableCell>
                        <TableCell>Prize won</TableCell>
                        {/* A head's figure is their team's net — the eye shows
                            who it came from. Only on a head-role challenge. */}
                        {hasTeams && <TableCell sx={{ width: 56 }}>Team</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((p, index) => (
                        <TableRow key={p.user_id} hover>
                          <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{p.user_name}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{p.role_label}</TableCell>
                          {band === 'all' && filterable && (
                            <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                              {bandHeading(p.group)}
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {formatChallengeValue(p.achieved, type)}
                          </TableCell>
                          <TableCell>
                            {/* The highest rung cleared — at most one prize. */}
                            <PrizeWonChip prize={p.prizes_won?.[0]?.prize} />
                          </TableCell>
                          {hasTeams && (
                            <TableCell>
                              {(p.members || []).length > 0 && (
                                <Tooltip title="See who contributed" arrow>
                                  <IconButton size="small" onClick={() => setTeamOf(p)}>
                                    <Icon icon="mdi:eye-outline" width={18} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          <TeamMembersDialog
            head={teamOf}
            type={type}
            open={Boolean(teamOf)}
            setOpen={(next) => !next && setTeamOf(null)}
          />
        </Box>
      }
    />
  );
}

BandBoardDialog.propTypes = {
  challenge: PropTypes.object,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};
